import React, { FC, useContext } from 'react';
import {
  CreateEditEstimateNavEnum,
  CreateEstimateButtonIds,
  ESTIMATE_ATTRIBUTE_TYPE
} from 'containers/estimates/constants';
import { CreateEstimateContextType } from 'containers/estimates/types';
import { CreateEstimateContext } from '../CreateEstimate';
import { CustomInput } from 'components';
import { NotifierTypes } from 'constants/notifierConstants';
import { selectCurrentTeam, showNotifier } from 'containers/app/appSlice';
import { useCheckEstimateReferenceNumberMutation } from 'containers/estimates/api';
import {
  selectEstimateReferenceNumber,
  selectInitialEstimateReferenceNumber,
  updateEstimateReferenceNumber
} from 'containers/estimates/estimatesSlice';
import { useAppDispatch, useAppSelector } from 'hooks';

interface EstimateReferenceNumberProps {
  className?: string;
}

const EstimateReferenceNumber: FC<EstimateReferenceNumberProps> = () => {
  const { setSelectedEstimateTab } = useContext<CreateEstimateContextType>(CreateEstimateContext);

  const selectedEstimateReferenceNumber = useAppSelector(selectEstimateReferenceNumber);
  const initialEstimateReferenceNumber = useAppSelector(selectInitialEstimateReferenceNumber);
  const currentTeam = useAppSelector(selectCurrentTeam);

  const dispatch = useAppDispatch();

  const [checkEstimateRefNumberQuery, { originalArgs: estimateRefMutationArguments }] =
    useCheckEstimateReferenceNumberMutation();

  const updateSelectedEstimateTab = () => {
    setSelectedEstimateTab(CreateEditEstimateNavEnum.MORE_OPTIONS);
  };

  const handleReferenceNumberChange = value => {
    dispatch(updateEstimateReferenceNumber(value));
  };

  const checkEstimateReferenceNumber = value => {
    const estimateRefNumberResult = checkEstimateRefNumberQuery({
      teamId: currentTeam.id,
      body: { value, attribute: ESTIMATE_ATTRIBUTE_TYPE.REFERENCE_NUMBER }
    }).unwrap();
    return estimateRefNumberResult;
  };

  const handleEstimateReferenceNumberCheck = async (e: FocusEvent) => {
    const elementRelatedTarget: HTMLInputElement = e.relatedTarget as HTMLInputElement;
    const estimatesButtonTargetArrayString: Array<string> = Object.values(CreateEstimateButtonIds);

    if (estimatesButtonTargetArrayString.includes(elementRelatedTarget?.id)) {
      return;
    }
    const estimateRefNumber = selectedEstimateReferenceNumber.trim();
    if (
      estimateRefNumber !== '' &&
      estimateRefNumber !== estimateRefMutationArguments?.body.value &&
      estimateRefNumber !== initialEstimateReferenceNumber.trim()
    ) {
      const { data } = await checkEstimateReferenceNumber(estimateRefNumber);
      const { isExists } = data ?? {};
      if (isExists) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: `Estimate reference number '${selectedEstimateReferenceNumber}'
              already exists in a different estimate.`
            },
            type: NotifierTypes.WARNING,
            showClose: true
          })
        );
      }
    }
  };

  return (
    <div>
      <div className={'mb-2 text-17px font-semibold'}>Estimate reference ID</div>
      <div>
        <CustomInput
          id="create-estimate-reference-number"
          maxLength={50}
          name="referenceNumber"
          placeholder="Enter estimate reference ID"
          value={selectedEstimateReferenceNumber ?? ''}
          onChange={handleReferenceNumberChange}
          className="py-[15px] text-17px font-normal text-primaryText outline-none 
          placeholder:text-[17px] placeholder:text-accentText"
          onFocus={updateSelectedEstimateTab}
          onBlur={handleEstimateReferenceNumberCheck}
          showRemainingChar={true}
        />
      </div>
    </div>
  );
};

export default EstimateReferenceNumber;
